// NGRP National Organisation — pan-India architecture, principles and expansion strategy.
// Authored by the party leadership. Rendered at /national.

export const NATIONAL = {
  title: "NGRP — A Pan-India Political Movement",
  subtitle: "One National Mission. Local Leadership. Accountable Governance.",
  intro: [
    "NGRP is being conceived not for one district, one state, one language, one community or one region.",
    "It is being built with a national vision for India \u2014 from villages to metropolitan cities, from Panchayats to Parliament \u2014 to create a new culture of democratic leadership based on knowledge, integrity, public service, performance, public trust and leadership.",
  ],

  motto_chain: "Knowledge \u2192 Integrity \u2192 Public Service \u2192 Performance \u2192 Public Trust \u2192 Leadership",

  eci_notice: {
    heading: "Legal distinction \u2014 pan-India party vs. formal \u2018National Party\u2019",
    body:
      "Being formed with a nationwide ambition is different from being formally recognised as a \u2018National Party\u2019 by the Election Commission of India. That recognition depends on electoral-performance criteria under Indian election law. During launch and until such recognition is formally obtained, NGRP describes itself as a pan-India political party/movement with a national organisational structure \u2014 not as a formally recognised National Party.",
  },

  architecture: [
    { key: "national", level: "National Council / National Executive", note: "Common national standards and constitutional principles." },
    { key: "state", level: "State & Union Territory Organisations", note: "State-specific policy and political leadership." },
    { key: "district", level: "District Organisations", note: "Operational management layer with functional teams." },
    { key: "lok_sabha", level: "Lok Sabha Constituency Organisations", note: "Coordination of Assembly-level teams; regional priorities." },
    { key: "assembly", level: "Assembly Constituency Organisations", note: "Permanent governance team beyond elections; candidate pipeline." },
    { key: "block", level: "Block / Taluka / Tehsil Organisations", note: "Coordinates clusters of local units." },
    { key: "municipal", level: "Municipal / Panchayat Organisations", note: "Aligned to constitutional local self-government geography." },
    { key: "ward", level: "Ward / Village Organisations", note: "Foundation unit \u2014 the NGRP Public Issues Register." },
    { key: "booth", level: "Booth Teams", note: "Electoral-roll-level presence and civic outreach." },
    { key: "citizens", level: "Citizens & Members", note: "The base of the institution \u2014 supporters, primary and active members, and leadership cadre." },
  ],

  principle: {
    name: "National Values \u2014 State Solutions \u2014 Local Leadership",
    body:
      "India\u2019s diversity is our strength. NGRP does not attempt to govern every state through one centralised political command. It maintains common national constitutional principles, while empowering state and local organisations to develop solutions appropriate to their people.",
    contrasts: [
      { region: "Punjab \u2194 Maharashtra", topic: "Agricultural challenges differ significantly." },
      { region: "Northeast \u2194 Gujarat", topic: "Development priorities and geographies differ." },
      { region: "Delhi / Mumbai / Bengaluru / Chennai / Hyderabad / Kolkata \u2194 rural districts", topic: "Urban challenges differ from rural ones." },
    ],
  },

  commitments: [
    "Government services are time-bound.",
    "Public expenditure is transparent.",
    "Corruption opportunities are systematically reduced.",
    "Citizens can track government applications and grievances.",
    "Political leadership is selected through transparent and merit-oriented processes.",
    "Young and capable citizens have a pathway into public leadership.",
    "Local governments are empowered and accountable.",
    "Technology simplifies government rather than complicating it.",
    "Government performance is measured through outcomes.",
    "The Constitution remains above every individual and political organisation.",
  ],

  responsibility_split: [
    {
      layer: "National organisation",
      body:
        "Establishes common standards for integrity, financial transparency, internal democracy, leadership development and constitutional values.",
    },
    {
      layer: "State organisations",
      body: "Develop state-specific policy programmes suited to local realities.",
    },
    {
      layer: "Local organisations",
      body: "Identify and work on local governance problems \u2014 village, ward, municipality and Panchayat level.",
    },
  ],

  goal: {
    heading: "Our Goal \u2014 an institution, not a personality",
    contrasts: [
      { avoid: "Personality-driven politics.", pursue: "Institution-driven democratic leadership." },
      { avoid: "Delhi controlling every decision.", pursue: "Strong national principles with empowered local leadership." },
      { avoid: "Politics only during elections.", pursue: "Public service every day." },
    ],
  },

  closing: {
    tagline: "Better Institutions. Better Governance. Stronger India.",
    hindi_tagline: "\u092d\u093e\u0930\u0924 \u0915\u0947 \u0939\u0930 \u0928\u093e\u0917\u0930\u093f\u0915 \u0915\u0947 \u0932\u093f\u090f \u2014 \u0938\u092e\u092f\u092c\u0926\u094d\u0927, \u092a\u093e\u0930\u0926\u0930\u094d\u0936\u0940 \u0914\u0930 \u091c\u0935\u093e\u092c\u0926\u0947\u0939 \u0936\u093e\u0938\u0928\u0964",
  },

  expansion_waves: [
    {
      wave: "Wave 0",
      title: "National foundation \u2014 immediate",
      body: "Establish the national constitution, national digital membership architecture and Ethics & Integrity Commission from day one \u2014 before any large field expansion.",
      when: "Days 1\u2013100",
    },
    {
      wave: "Wave 1",
      title: "First-state model \u2014 Maharashtra pilot",
      body: "Prove the operating model in a single state with dense founder coverage, pilot districts, village units and a public Governance Reform Report.",
      when: "Days 1\u2013100 (pilot state)",
    },
    {
      wave: "Wave 2",
      title: "Founding councils in additional states & UTs",
      body: "Credible founding councils established in 6\u201310 additional states/UTs \u2014 by invitation, not press announcement.",
      when: "Months 4\u201312",
    },
    {
      wave: "Wave 3",
      title: "District & constituency organisations at scale",
      body: "Only after Wave 2 states demonstrate real activity: village/ward units, Public Issues Registers, monthly reports.",
      when: "Year 2",
    },
    {
      wave: "Wave 4",
      title: "National expansion to remaining states & UTs",
      body: "Sequenced by state-specific readiness and credible local leadership \u2014 not artificial simultaneity.",
      when: "Year 3",
    },
  ],

  expansion_principle:
    "We do not attempt to activate every state simultaneously. We avoid creating hundreds of paper committees with no ground activity. Wave-based expansion is deliberate.",

  next_document:
    "The next document to prepare is the NGRP National Organisation Master Plan \u2014 covering all States and Union Territories, national and state powers, state-president structure, district and constituency hierarchy, multilingual organisation, membership targets, national leadership academy and a 3-year India expansion roadmap.",
};
